import { Question, Section, SectionFlowState } from '../types/form-engine.types';
import { validateQuestionAnswer } from './form-validation';

type Navigation = 'end' | 'continue_and_end' | { jumpTo: string } | null;

export const createSectionFlowState = (sections: Section[]): SectionFlowState => ({
  currentSectionIndex: 0,
  visitedSections: sections.length ? [sections[0].SectionId] : [],
  skippedSections: [],
  isFormEnded: false,
  endedAtQuestionId: undefined,
  shouldEndAfterCurrentSection: false,
  jumpedFromIndex: null,
});

const validateSection = (section?: Section): string[] =>
  (section?.Questions ?? [])
    .filter((question) => question.IsRequired)
    .map((question) => validateQuestionAnswer(question))
    .filter((error): error is string => Boolean(error));

const selectedOption = (question: Question) =>
  question.FormatType === 'multiple-choice' && question.McOptionsValue
    ? question.Options?.find((option) => option.Value === question.McOptionsValue)
    : undefined;

const readNavigation = (section: Section): { navigation: Navigation; endedAtQuestionId?: string } => {
  for (const question of section.Questions) {
    const option = selectedOption(question);
    if (option?.Action === 'end') return { navigation: 'end', endedAtQuestionId: question.ItemId };
    if (option?.Action === 'continue_and_end') return { navigation: 'continue_and_end', endedAtQuestionId: question.ItemId };
    if (option?.Action === 'jump_to_section' && option.TargetSectionId) {
      return { navigation: { jumpTo: option.TargetSectionId } };
    }
  }
  return { navigation: null };
};

const addSkipped = (skipped: string[], sections: Section[], fromIndexInclusive: number, toIndexExclusive: number) => {
  const next = [...skipped];
  for (let index = fromIndexInclusive; index < toIndexExclusive; index += 1) {
    const id = sections[index].SectionId;
    if (!next.includes(id)) next.push(id);
  }
  return next;
};

const visit = (state: SectionFlowState, section?: Section) =>
  section && !state.visitedSections.includes(section.SectionId)
    ? [...state.visitedSections, section.SectionId]
    : state.visitedSections;

const endForm = (state: SectionFlowState, sections: Section[], endedAtQuestionId?: string): SectionFlowState => ({
  ...state,
  isFormEnded: true,
  endedAtQuestionId,
  skippedSections: addSkipped(state.skippedSections, sections, state.currentSectionIndex + 1, sections.length),
});

const goToNextSection = (state: SectionFlowState, sections: Section[]): SectionFlowState => {
  if (state.currentSectionIndex >= sections.length - 1) return state;
  const nextIndex = state.currentSectionIndex + 1;
  return { ...state, currentSectionIndex: nextIndex, visitedSections: visit(state, sections[nextIndex]) };
};

/** Validates the current section then applies the conditional-logic action of the chosen multiple-choice option. */
export const goNext = (
  state: SectionFlowState,
  sections: Section[]
): { state: SectionFlowState; errors: string[] } => {
  const current = sections[state.currentSectionIndex];
  const errors = validateSection(current);
  if (errors.length) return { state, errors };

  if (state.shouldEndAfterCurrentSection) {
    return { state: endForm({ ...state, shouldEndAfterCurrentSection: false }, sections), errors: [] };
  }

  const { navigation, endedAtQuestionId } = readNavigation(current);

  if (navigation === 'end') return { state: endForm(state, sections, endedAtQuestionId), errors: [] };

  if (navigation === 'continue_and_end') {
    const nextIndex = state.currentSectionIndex + 1;
    const marked: SectionFlowState = {
      ...state,
      endedAtQuestionId,
      shouldEndAfterCurrentSection: true,
      skippedSections: addSkipped(state.skippedSections, sections, nextIndex + 1, sections.length),
    };
    return { state: goToNextSection(marked, sections), errors: [] };
  }

  if (navigation && 'jumpTo' in navigation) {
    const targetIndex = sections.findIndex((section) => section.SectionId === navigation.jumpTo);
    if (targetIndex === -1) return { state: goToNextSection(state, sections), errors: [] };

    const skipped =
      targetIndex > state.currentSectionIndex
        ? addSkipped(state.skippedSections, sections, state.currentSectionIndex + 1, targetIndex)
        : state.skippedSections;
    return {
      state: {
        ...state,
        jumpedFromIndex: state.currentSectionIndex,
        currentSectionIndex: targetIndex,
        visitedSections: visit(state, sections[targetIndex]),
        skippedSections: skipped.filter((id) => id !== navigation.jumpTo),
      },
      errors: [],
    };
  }

  return { state: goToNextSection(state, sections), errors: [] };
};

const resetEndState = (state: SectionFlowState): SectionFlowState => ({
  ...state,
  isFormEnded: false,
  endedAtQuestionId: undefined,
  shouldEndAfterCurrentSection: false,
});

export const goPrevious = (state: SectionFlowState): SectionFlowState => {
  if (state.currentSectionIndex === 0) return state;
  const target = state.jumpedFromIndex ?? state.currentSectionIndex - 1;
  return { ...resetEndState(state), skippedSections: [], jumpedFromIndex: null, currentSectionIndex: Math.max(target, 0) };
};

/** Undo an "end form" decision while staying on the current section. */
export const reopenEndedForm = (state: SectionFlowState, sections: Section[]): SectionFlowState => {
  const laterIds = sections.slice(state.currentSectionIndex + 1).map((section) => section.SectionId);
  return {
    ...resetEndState(state),
    jumpedFromIndex: null,
    skippedSections: state.skippedSections.filter((id) => !laterIds.includes(id)),
  };
};

export const goToSection = (state: SectionFlowState, sections: Section[], index: number): SectionFlowState => {
  const target = sections[index];
  if (!target) return state;
  const isSkipped = state.skippedSections.includes(target.SectionId);
  if ((isSkipped && !state.isFormEnded) || index > state.currentSectionIndex) return state;

  if (index < state.currentSectionIndex) {
    const laterIds = sections.slice(index + 1).map((section) => section.SectionId);
    return {
      ...resetEndState(state),
      skippedSections: state.isFormEnded ? state.skippedSections.filter((id) => !laterIds.includes(id)) : state.skippedSections,
      currentSectionIndex: index,
    };
  }
  return { ...state, currentSectionIndex: index };
};

/** Validates every section the reviewer actually visited; skipped sections are exempt, unvisited ones are errors. */
export const validateAllVisited = (
  state: SectionFlowState,
  sections: Section[]
): { state: SectionFlowState; errors: string[]; errorsBySection: Record<string, string[]>; firstErrorIndex: number } => {
  let nextState = state;

  if (!state.isFormEnded) {
    const current = sections[state.currentSectionIndex];
    if (current && validateSection(current).length === 0) {
      const { navigation, endedAtQuestionId } = readNavigation(current);
      if (navigation === 'end') nextState = endForm(state, sections, endedAtQuestionId);
    }
  }

  const errorsBySection: Record<string, string[]> = {};
  const errors: string[] = [];

  sections.forEach((section) => {
    if (nextState.skippedSections.includes(section.SectionId)) {
      errorsBySection[section.SectionId] = [];
      return;
    }
    if (!nextState.visitedSections.includes(section.SectionId)) {
      const message = 'Section must be completed before submitting';
      errorsBySection[section.SectionId] = [message];
      errors.push(message);
      return;
    }
    const sectionErrors = validateSection(section);
    errorsBySection[section.SectionId] = sectionErrors;
    errors.push(...sectionErrors);
  });

  const firstErrorIndex = sections.findIndex((section) => errorsBySection[section.SectionId]?.length);
  if (firstErrorIndex !== -1) nextState = { ...nextState, currentSectionIndex: firstErrorIndex };

  return { state: nextState, errors, errorsBySection, firstErrorIndex };
};
